import { INT64_RANGE_MESSAGE, int64Validator } from 'src/validators/integer.validator';
import {
    ValueDescriptionAnchor,
    ValueDescriptionVersion,
} from 'src/models/anchor-and-version/value-description.model';

export interface SignalValueDescriptionRow {
    anchorKey?: number;
    comment: string;
    description: string;
    isActive: boolean;
    isNew: boolean;
    name: string;
    originalDescription?: string;
    originalIsActive?: boolean;
    originalName?: string;
    originalValue?: string | null;
    rowId: string;
    value: string | null;
}

export type ExistingSignalValueDescriptionRow = SignalValueDescriptionRow & {
    anchorKey: number;
};

export function createNewValueDescriptionRow(rowId: string): SignalValueDescriptionRow {
    return {
        comment: '',
        description: '',
        isActive: true,
        isNew: true,
        name: '',
        rowId,
        value: null,
    };
}

export function createRowsFromAnchors(
    anchors: ValueDescriptionAnchor[]
): SignalValueDescriptionRow[] {
    return anchors
        .map((anchor) => createRow(anchor))
        .filter((row): row is SignalValueDescriptionRow => row !== null)
        .sort((a, b) => Number(BigInt(a.value ?? '0') - BigInt(b.value ?? '0')));
}

export function createValueDescriptionAnchorPayload(
    row: SignalValueDescriptionRow,
    signalAnchorKey: number,
    changeRequestKey: number
): Record<string, unknown> {
    return {
        logText: '',
        signalAnchorKey,
        allVersions: [createValueDescriptionVersionPayload(row, 0, changeRequestKey)],
    };
}

export function createValueDescriptionVersionPayload(
    row: SignalValueDescriptionRow,
    anchorKey: number,
    changeRequestKey: number
): Record<string, unknown> {
    return {
        anchorKey,
        changeRequestKey,
        comment: row.comment,
        description: row.description,
        isActive: Boolean(row.isActive),
        logText: '',
        name: row.name || row.description,
        value: row.value ?? '0',
    };
}

export function getDescriptionText(row: SignalValueDescriptionRow): string {
    return row.description || row.name || '';
}

export function getRowsToSave(rows: SignalValueDescriptionRow[]): {
    changedRows: ExistingSignalValueDescriptionRow[];
    newRows: SignalValueDescriptionRow[];
} {
    return {
        newRows: rows.filter((row) => row.isNew),
        changedRows: rows.filter(
            (row): row is ExistingSignalValueDescriptionRow =>
                !row.isNew && typeof row.anchorKey === 'number' && hasRowChanged(row)
        ),
    };
}

export function getValueDescriptionValidationMessage(
    rows: SignalValueDescriptionRow[]
): string | undefined {
    if (hasEmptyRequiredFields(rows)) {
        return 'Value description rows must have a value and description';
    }

    for (const row of rows) {
        const error = int64Validator({ value: row.value });
        if (error) {
            return error['integer']
                ? 'Value description values must be integers'
                : INT64_RANGE_MESSAGE;
        }
    }

    if (hasDuplicateValues(rows)) {
        return 'Value description values must be unique';
    }

    return undefined;
}

export function parseRowValue(value: unknown): string | null {
    return String(value ?? '').trim() || null;
}

function createRow(anchor: ValueDescriptionAnchor): SignalValueDescriptionRow | null {
    const latestVersion = anchor.validVersion ?? getNewestVersion(anchor.allVersions ?? []);
    if (!latestVersion) return null;

    const value = parseRowValue(latestVersion.value ?? '0');

    return {
        anchorKey: anchor.key,
        comment: latestVersion.comment ?? '',
        description: latestVersion.description ?? '',
        isActive: Boolean(latestVersion.isActive),
        isNew: false,
        name: latestVersion.name ?? '',
        originalDescription: latestVersion.description ?? '',
        originalIsActive: Boolean(latestVersion.isActive),
        originalName: latestVersion.name ?? '',
        originalValue: value,
        rowId: `existing-${anchor.key}`,
        value,
    };
}

function getNewestVersion(
    versions: ValueDescriptionVersion[]
): ValueDescriptionVersion | undefined {
    return [...versions].sort((a, b) => getVersionSortValue(b) - getVersionSortValue(a))[0];
}

function getVersionSortValue(version: ValueDescriptionVersion): number {
    const timestamp = version.createdTimestamp ? new Date(version.createdTimestamp).getTime() : 0;
    return timestamp || version.key || 0;
}

function hasDuplicateValues(rows: SignalValueDescriptionRow[]): boolean {
    const values = new Set<string>();

    return rows.some((row) => {
        if (row.value === null || row.value === undefined) return false;

        const value = BigInt(row.value).toString();

        if (values.has(value)) return true;

        values.add(value);
        return false;
    });
}

function hasEmptyRequiredFields(rows: SignalValueDescriptionRow[]): boolean {
    return rows.some((row) => {
        const value = row.value;
        const hasEmptyValue = value === null || value === undefined || value.trim() === '';
        const hasEmptyDescription = getDescriptionText(row).trim() === '';

        return hasEmptyValue || hasEmptyDescription;
    });
}

function hasRowChanged(row: SignalValueDescriptionRow): boolean {
    if (row.isNew) return true;

    return (
        row.value !== row.originalValue ||
        row.description !== row.originalDescription ||
        row.name !== row.originalName ||
        row.isActive !== row.originalIsActive
    );
}
